import React, { useEffect } from "react";

import { Route, RouteComponentProps, Switch } from "react-router-dom";

import { useGetReport } from "../../graphql/generated/definitions";

import useStore from "../../store";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";

import ErrorBoundaryScreen from "../../screens/ErrorBoundaryScreen";

import WelcomePage from "./pages/WelcomePage";
import SuccessPage from "./pages/SuccessPage";
import LeavingPage from "./pages/LeavingPage";
import ViewPage from "./pages/ViewPage";
import EditPage from "./pages/EditPage";
import ReportPrint from "./printer/ReportPrint";
import { useUnmount } from "react-use";

type ReportPageProps = RouteComponentProps<{ reportId: string }>;

const ReportPage: React.FC<ReportPageProps> = ({
  match,
  history,
  location,
}) => {
  const initializeReportState = useStore(
    (state) => state.report.initializeReportState
  );
  const rehydrateReportState = useStore(
    (state) => state.report.rehydrateReportState
  );
  const resetStore = useStore((state) => state.report.reset);

  const [, reportState] = useStore((state) => state.report.viewedReportState);
  const currentStep = useStore((state) => state.report.currentStep);

  useUnmount(resetStore);

  const {
    data: reportData,
    loading: reportLoading,
    error: reportError,
    refetch,
  } = useGetReport({
    variables: { id: match.params.reportId },
    onCompleted: (data) => initializeReportState(data.report),
    nextFetchPolicy: "standby",
    // Ensure that the cache is updated when the result of the server-side query modifies cached data
    // Allow for a great rerender count while on edit mode & to keep data in sync with the server on remount
    fetchPolicy: "network-only",
  });

  // Rehydrate the store every time the report is refetch, ensuring that the store is hydrated with the latest answers sent
  useEffect(() => {
    if (reportData?.report) {
      rehydrateReportState(reportData.report);
    }
  }, [reportData?.report]);

  // Force Apollo to refetch throught the network the latest answers sent when switching between steps
  // This is important because this component won't be unmounted during those switches leading to the store to be rehydrated with old data
  useEffect(() => {
    refetch();
  }, [currentStep]);

  useEffect(() => {
    if (!reportData?.report) return;

    const reportBaseUrl = `/reports/${reportData.report.id}`;

    if (currentStep === "welcome")
       history.replace(`${reportBaseUrl}/welcome`);

    if (currentStep === "report" && reportState === "view") {
      // Add the search params to account for the signature process
      history.replace(`${reportBaseUrl}/view${location?.search || ""}`);
    }

    if (currentStep === "report" && reportState === "edit")
      history.replace(`${reportBaseUrl}/edit`);

    if (currentStep === "success")
      history.replace(`${reportBaseUrl}/success`);

    if (currentStep === "leaving")
      history.replace(`${reportBaseUrl}/leaving`);

  }, [reportState, currentStep, history, reportData]);

  // Used only to change the title of the screen
  useBreadcrumbs([
    {
      to: `/reviews/${reportData?.report?.review.id}`,
      text: reportData?.report?.review.template.title,
    },
  ]);

  if (reportError) return <ErrorBoundaryScreen type="UnexpectedError" />;

  if (
    reportLoading ||
    !reportData?.report ||
    !reportState /* Ensure that the store is initialized before rendering */
  ) {
    return null;
  }

  return (
    <>
      <Switch>
        <Route path={`/reports/${match.params.reportId}/welcome`} exact>
          <WelcomePage />
        </Route>
        <Route path={`/reports/${match.params.reportId}/edit`} exact>
          <EditPage report={reportData.report} />
        </Route>
        <Route path={`/reports/${match.params.reportId}/view`} exact>
          <ViewPage report={reportData.report} />
        </Route>
        <Route path={`/reports/${match.params.reportId}/success`} exact>
          <SuccessPage />
        </Route>
        <Route path={`/reports/${match.params.reportId}/leaving`} exact>
          <LeavingPage report={reportData.report} />
        </Route>
      </Switch>
      <ReportPrint report={reportData.report} />
    </>
  );
};

export default ReportPage;
